'use client';

import { Box, SystemIcon } from '@wishket/design-system';
import { useRef, useState } from 'react';
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper/modules';

import { AuthorProfileLinkCard } from '../Author';

import { useDeviceCheck } from '@/shared/hooks';
import { AuthorListCardProps } from '@/shared/types';
import { FadeUpContainer } from '@/shared/Components';

interface SearchedAuthorProps {
  authors: AuthorListCardProps[];
}

const SearchedAuthors = ({ authors }: SearchedAuthorProps) => {
  const { isDesktop } = useDeviceCheck();
  const swiperRef = useRef<SwiperRef>(null);
  const [swiper, setSwiper] = useState<SwiperClass>();
  const nextRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<HTMLDivElement>(null);

  const navigationParam = {
    nextEl: nextRef.current,
    prevEl: prevRef.current,
  };

  if (isDesktop) {
    return (
      <>
        <Box className="flex flex-1 items-center grid-in-content">
          <Swiper
            speed={800}
            ref={swiperRef}
            slidesPerGroup={1}
            slidesPerView="auto"
            className="swiper-wrapper"
            spaceBetween={24}
            onSwiper={setSwiper}
            modules={[Navigation, FreeMode]}
            navigation={navigationParam}
            freeMode={{
              enabled: !isDesktop,
              momentum: false,
            }}
          >
            {authors.map((author) => (
              <SwiperSlide
                key={author.id}
                className="swiper-slide !w-[calc((100%_-_48px)/3)]"
              >
                <AuthorProfileLinkCard
                  name={author.name}
                  description={author.tagLine}
                  imgUrl={author.imageUrl}
                  urlIdentifier={author.urlIdentifier}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
        <Box
          ref={prevRef}
          className="flex items-center justify-end grid-in-prevBtn"
        >
          <button>
            <Box className="flex size-8 items-center justify-center rounded-full border border-w-gray-200 bg-white p-2">
              <SystemIcon
                name="medium_arrow_left"
                className="text-w-gray-600"
              />
            </Box>
          </button>
        </Box>
        <Box
          ref={nextRef}
          className="flex items-center justify-start grid-in-nextBtn"
        >
          <button>
            <Box className="flex size-8 items-center justify-center rounded-full border border-w-gray-200 p-2">
              <SystemIcon
                name="medium_arrow_right"
                className="text-w-gray-600"
              />
            </Box>
          </button>
        </Box>
      </>
    );
  }

  return (
    <FadeUpContainer
      className="flex w-full gap-5 self-start overflow-x-scroll px-5 scrollbar-hide tablet:gap-6 desktop:px-0"
      delay={0.2}
    >
      {authors.map((author) => (
        <AuthorProfileLinkCard
          key={author.id}
          name={author.name}
          description={author.tagLine}
          imgUrl={author.imageUrl}
          urlIdentifier={author.urlIdentifier}
          isFixedSize={isDesktop}
        />
      ))}
    </FadeUpContainer>
  );
};

export default SearchedAuthors;
